import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { OrgAdminUnauthorizedError } from '@/api/orgAdmin';
import { fetchGlobalSearch, SEARCH_MIN_QUERY_LENGTH, SEARCH_RESULTS_PER_TYPE } from '@/api/shell';
import { Kbd } from '@/components/ui';
import { buildPaletteGroups } from './commandPaletteRows';
import './CommandPalette.css';

/** Long enough that a 4-word query is one request, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * EAA-1813: the ⌘K global-search palette (handoff frame 13), replacing the
 * `disabled` topbar search box that shipped with an sr-only "chưa khả dụng"
 * note because no endpoint existed. `GET /api/v1/search` (EAA-1831) is that
 * endpoint; `src/api/shell.ts` is its client.
 *
 * Mounted only while open — `useCommandPalette` owns the open state — so the
 * query text, debounce timer and active index all reset on each open without
 * this component having to unwind them itself.
 *
 * Keyboard model is frame 13's: results are grouped in the DOM but arrow keys
 * walk ALL of them as one flat list, and focus never leaves the input (the rows
 * are `role="option"` with `aria-activedescendant` virtual focus, not tab
 * stops) — so ↑↓ can never be swallowed by a focused row.
 */
export function CommandPalette({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { authenticated, accessToken, reauthenticate } = useAuth();
    const navigate = useNavigate();

    const inputRef = useRef<HTMLInputElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    const [queryInput, setQueryInput] = useState('');
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const handle = setTimeout(() => setQuery(queryInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [queryInput]);

    // EAA-1813: a dialog opened by a keyboard chord has to move focus into
    // itself, and hand it back on close — otherwise ⌘K then Escape leaves the
    // caret nowhere and the next Tab restarts from the top of the document.
    useEffect(() => {
        const previouslyFocused = document.activeElement as HTMLElement | null;
        inputRef.current?.focus();
        return () => previouslyFocused?.focus?.();
    }, []);

    const longEnough = query.length >= SEARCH_MIN_QUERY_LENGTH;
    const enabled = authenticated && Boolean(accessToken) && longEnough;

    const searchQuery = useQuery({
        queryKey: ['global-search', query, accessToken],
        queryFn: () => fetchGlobalSearch(config.apiBase, accessToken as string, query),
        enabled,
        retry: false,
    });

    // EAA-1813: same 401 convention the console pages already follow — a typed
    // unauthorized error re-runs sign-in rather than rendering a failure state.
    // EAA-1825: through `reauthenticate`, not `client.login()` — the palette fires
    // in the same 401 burst as the shell and the launcher, and three unguarded
    // login() calls raced to reload the page (and burned the retry budget in one
    // leg). The guard collapses them into one attempt.
    useEffect(() => {
        if (searchQuery.error instanceof OrgAdminUnauthorizedError) {
            reauthenticate(window.location.pathname);
        }
    }, [searchQuery.error, reauthenticate]);

    const groups = useMemo(() => buildPaletteGroups(searchQuery.data?.results), [searchQuery.data]);
    const rows = useMemo(() => groups.flatMap((group) => group.rows), [groups]);

    useEffect(() => {
        setActiveIndex(0);
    }, [rows]);

    // Keeps the virtually-focused row visible while arrowing past the fold.
    useEffect(() => {
        bodyRef.current
            ?.querySelector('[data-active="true"]')
            ?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const openRow = (target: string) => {
        onClose();
        navigate(target);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => Math.min(index + 1, rows.length - 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const row = rows[activeIndex];
            if (row) openRow(row.target);
        }
    };

    let body: ReactNode;
    if (!longEnough) {
        body = (
            <p className="portal-cmdk-hint">
                {t('shell.commandPalette.minLengthHint', { count: SEARCH_MIN_QUERY_LENGTH })}
            </p>
        );
    } else if (searchQuery.isError) {
        body = (
            <p className="portal-cmdk-hint portal-cmdk-hint--error">
                {t('shell.commandPalette.searchError')}
            </p>
        );
    } else if (searchQuery.isFetching && rows.length === 0) {
        body = <p className="portal-cmdk-hint">{t('shell.commandPalette.searching')}</p>;
    } else if (rows.length === 0) {
        body = (
            <p className="portal-cmdk-hint">
                {t('shell.commandPalette.noResults', { query })}
            </p>
        );
    } else {
        body = groups.map((group) => (
            <div
                key={group.key}
                className="portal-cmdk-group"
                role="group"
                aria-labelledby={`portal-cmdk-group-${group.key}`}
            >
                <div className="portal-cmdk-group-label" id={`portal-cmdk-group-${group.key}`}>
                    {group.label}
                </div>
                {group.rows.map((row, rowIndex) => {
                    const index = group.offset + rowIndex;
                    const active = index === activeIndex;
                    return (
                        <div
                            key={row.key}
                            id={`portal-cmdk-option-${index}`}
                            className={'portal-cmdk-row' + (active ? ' is-active' : '')}
                            role="option"
                            aria-selected={active}
                            data-active={active || undefined}
                            onMouseMove={() => setActiveIndex(index)}
                            onClick={() => openRow(row.target)}
                        >
                            <span className="portal-cmdk-tile" aria-hidden="true">
                                {row.initials}
                            </span>
                            <span className="portal-cmdk-text">
                                <span className="portal-cmdk-title">{row.title}</span>
                                {row.caption && (
                                    <span className="portal-cmdk-caption">{row.caption}</span>
                                )}
                            </span>
                            {row.meta && <span className="portal-cmdk-meta">{row.meta}</span>}
                            {active && (
                                <span className="portal-cmdk-enter" aria-hidden="true">
                                    {t('shell.commandPalette.enterHint')}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        ));
    }

    const activeRowId = rows[activeIndex] ? `portal-cmdk-option-${activeIndex}` : undefined;

    return (
        <div
            className="portal-cmdk-scrim"
            role="presentation"
            // The scrim covers the whole viewport, so a mousedown that lands on
            // it *is* a click outside the panel — no document-level listener and
            // no contains() test needed.
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div
                className="portal-cmdk"
                role="dialog"
                aria-modal="true"
                aria-label={t('shell.search.ariaLabel')}
                onKeyDown={handleKeyDown}
            >
                <div className="portal-cmdk-head">
                    <SearchIcon />
                    <input
                        ref={inputRef}
                        className="portal-cmdk-input"
                        type="text"
                        value={queryInput}
                        onChange={(event) => setQueryInput(event.target.value)}
                        placeholder={t('shell.commandPalette.searchPlaceholder')}
                        aria-label={t('shell.search.ariaLabel')}
                        role="combobox"
                        aria-expanded={rows.length > 0}
                        aria-controls="portal-cmdk-list"
                        aria-activedescendant={activeRowId}
                        aria-autocomplete="list"
                        autoComplete="off"
                        spellCheck={false}
                    />
                    <span aria-hidden="true">
                        <Kbd>esc</Kbd>
                    </span>
                </div>
                {/* EAA-1813: `role="listbox"` only while there are real options
                    to own. The hint/empty/error states render a <p>, and a
                    listbox whose only child is a paragraph is invalid ARIA — a
                    screen reader would announce an empty list of options
                    instead of reading the message. */}
                <div
                    ref={bodyRef}
                    className="portal-cmdk-body"
                    id="portal-cmdk-list"
                    role={rows.length > 0 ? 'listbox' : undefined}
                    aria-label={rows.length > 0 ? t('shell.commandPalette.resultsAriaLabel') : undefined}
                    aria-live={rows.length > 0 ? undefined : 'polite'}
                >
                    {body}
                </div>
                <div className="portal-cmdk-foot">
                    <span className="portal-cmdk-foot-hints">
                        <span>
                            <Kbd>↑↓</Kbd> {t('shell.commandPalette.moveHint')}
                        </span>
                        <span>
                            <Kbd>↵</Kbd> {t('shell.commandPalette.selectHint')}
                        </span>
                    </span>
                    {/* EAA-1813: the scope caveat states only what the endpoint
                        actually guarantees (api/shell.ts): the server narrows
                        results to the caller's own admin grants, and caps each
                        group at RESULTS_PER_TYPE. */}
                    <span className="portal-cmdk-foot-note">
                        {t('shell.commandPalette.scopeNote', { count: SEARCH_RESULTS_PER_TYPE })}
                    </span>
                </div>
            </div>
        </div>
    );
}

/* Inline icon — no icon dependency, matching the topbar/nav convention. */
function SearchIcon() {
    return (
        <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden="true"
        >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
        </svg>
    );
}
